import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { maturationService } from '../../../services/maturationService';
import { calculateGrowthVelocity, detectPHVWithConfidence, classifyGrowthPhaseWithConfidence, calculateAgeInYears, assessDataQualityWithHardRequirements, calculateMaturityOffset, calculatePercentPredictedAdultHeight } from '../../../utils/growthCalculations';
import { getStageMeaning, getTrainingPriorities, getExampleWeekTemplates, exampleSessions, getLoadManagementRules, safetyCaveats, evidenceReferences } from '../../../utils/trainingGuidanceContent';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line } from 'recharts';

const InsightsScreen = ({ child, onBack, heightChartRef, velocityChartRef }) => {
  const [loading, setLoading] = useState(true);
  const [phvResult, setPhvResult] = useState(null);
  const [growthPhase, setGrowthPhase] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [maturityOffset, setMaturityOffset] = useState(null);
  const [percentAdultHeight, setPercentAdultHeight] = useState(null);
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [estimatesDisagree, setEstimatesDisagree] = useState(false);
  const [trainingExperience, setTrainingExperience] = useState('some_experience');
  const [expandedSessions, setExpandedSessions] = useState([]);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [growthData, setGrowthData] = useState(null);

  useEffect(() => {
    loadInsights();
  }, [child?.id]);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const measurements = await maturationService?.getMeasurements(child?.id);
      
      if (measurements?.length > 0) {
        const sorted = [...measurements]?.sort((a, b) => 
          new Date(b?.measurementDatetime) - new Date(a?.measurementDatetime)
        );
        const latest = sorted?.[0];
        setLatestMeasurement(latest);

        const { heightVsAge, velocityVsAge, suspiciousIntervals } = calculateGrowthVelocity(measurements, child?.dateOfBirth);
        setGrowthData({ heightVsAge, velocityVsAge });
        const currentAge = calculateAgeInYears(child?.dateOfBirth, new Date());
        
        const quality = assessDataQualityWithHardRequirements(measurements, child?.dateOfBirth, child?.sexAtBirth, suspiciousIntervals);
        setDataQuality(quality);

        const phv = detectPHVWithConfidence(velocityVsAge, child?.sexAtBirth, currentAge, quality);
        setPhvResult(phv);

        const phase = classifyGrowthPhaseWithConfidence(phv, currentAge, quality);
        setGrowthPhase(phase);

        // Secondary maturity estimation tools
        if (latest?.standingHeightCm && latest?.bodyMassKg) {
          const offset = calculateMaturityOffset(
            parseFloat(latest?.standingHeightCm),
            latest?.sittingHeightCm ? parseFloat(latest?.sittingHeightCm) : null,
            parseFloat(latest?.bodyMassKg),
            currentAge,
            child?.sexAtBirth
          );
          setMaturityOffset(offset);

          // Check for disagreement
          if (offset && phv?.status === 'detected') {
            const phvEstimate = phv?.phvAge;
            const offsetEstimate = offset?.estimatedPHVAge;
            const difference = Math.abs(phvEstimate - offsetEstimate);
            if (difference > 1.5) {
              setEstimatesDisagree(true);
            }
          }
        }

        // Percent predicted adult height (only if parental heights available)
        if (child?.motherHeightCm && child?.fatherHeightCm && latest?.standingHeightCm) {
          const percentHeight = calculatePercentPredictedAdultHeight(
            parseFloat(latest?.standingHeightCm),
            currentAge,
            child?.sexAtBirth,
            {
              motherHeightCm: child?.motherHeightCm,
              fatherHeightCm: child?.fatherHeightCm
            }
          );
          setPercentAdultHeight(percentHeight);
        }
      }
    } catch (err) {
      console.error('Error loading insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSessionExpanded = (sessionId) => {
    setExpandedSessions(prev => 
      prev?.includes(sessionId) 
        ? prev?.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const exportToPDF = async () => {
    try {
      setExportingPDF(true);
      const doc = new jsPDF();
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper to check if we need a new page
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPos + requiredSpace > pageHeight - margin) {
          doc?.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Helper to add section header (monochrome)
      const addSectionHeader = (title) => {
        checkPageBreak(15);
        doc?.setFillColor(240, 240, 240);
        doc?.rect(margin, yPos, maxWidth, 8, 'F');
        doc?.setFontSize(12);
        doc?.setFont('helvetica', 'bold');
        doc?.setTextColor(0, 0, 0);
        doc?.text(title, margin + 2, yPos + 5.5);
        yPos += 12;
      };

      // Helper to add text with proper wrapping
      const addWrappedText = (text, fontSize = 10, isBold = false, leftMargin = margin, lineSpacing = 1.2) => {
        doc?.setFontSize(fontSize);
        doc?.setFont('helvetica', isBold ? 'bold' : 'normal');
        const lines = doc?.splitTextToSize(text, maxWidth - (leftMargin - margin));
        lines?.forEach(line => {
          checkPageBreak(fontSize * 0.5);
          doc?.text(line, leftMargin, yPos);
          yPos += fontSize * 0.35 * lineSpacing;
        });
      };

      // Add header (monochrome)
      doc?.setFillColor(0, 0, 0);
      doc?.rect(0, 0, pageWidth, 25, 'F');
      doc?.setTextColor(255, 255, 255);
      doc?.setFontSize(18);
      doc?.setFont('helvetica', 'bold');
      doc?.text('Growth & Training Insights Report', margin, 12);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      doc?.text(`Generated: ${new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, 19);
      yPos = 35;

      doc?.setTextColor(0, 0, 0);

      // Child Profile Section
      addSectionHeader('Child Profile');
      const profileData = [
        ['Name', child?.nickname],
        ['Date of Birth', new Date(child?.dateOfBirth)?.toLocaleDateString('en-GB')],
        ['Sex at Birth', child?.sexAtBirth === 'female' ? 'Female' : 'Male'],
        ['Current Age', `${calculateAgeInYears(child?.dateOfBirth, new Date())?.toFixed(1)} years`],
      ];
      if (child?.sportTags?.length > 0) {
        profileData?.push(['Sports', child?.sportTags?.join(', ')]);
      }

      doc?.autoTable({
        startY: yPos,
        body: profileData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 2, textColor: [0, 0, 0] },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 45 },
          1: { cellWidth: 'auto' }
        },
        margin: { left: margin, right: margin }
      });
      yPos = doc?.lastAutoTable?.finalY + 10;

      // Measurement Summary Section
      addSectionHeader('Measurement Summary');
      const measurementData = [
        ['Confidence Grade', dataQuality?.grade || 'Unknown'],
        ['Confidence Level', dataQuality?.confidence || 'Unknown'],
      ];
      if (latestMeasurement) {
        measurementData?.push(
          ['Latest Height', `${latestMeasurement?.standingHeightCm} cm`],
          ['Measurement Date', new Date(latestMeasurement?.measurementDatetime)?.toLocaleDateString('en-GB')]
        );
      }
      if (phvResult?.status === 'detected') {
        measurementData?.push(
          ['Estimated PHV Age', `${phvResult?.phvAge?.toFixed(1)} years`],
          ['Peak Velocity', `${phvResult?.peakVelocity?.toFixed(1)} cm/year`]
        );
      }

      doc?.autoTable({
        startY: yPos,
        body: measurementData,
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 2, textColor: [0, 0, 0] },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 50 },
          1: { cellWidth: 'auto' }
        },
        margin: { left: margin, right: margin }
      });
      yPos = doc?.lastAutoTable?.finalY + 10;

      // Capture and add charts from visible GrowthDashboard
      if (heightChartRef?.current && growthData?.heightVsAge?.length > 0) {
        checkPageBreak(80);
        
        try {
          // Capture height chart from actual visible chart
          const heightCanvas = await html2canvas(heightChartRef?.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: false
          });
          const heightImgData = heightCanvas?.toDataURL('image/png');
          
          doc?.setFontSize(11);
          doc?.setFont('helvetica', 'bold');
          doc?.text('Height Over Time', margin, yPos);
          yPos += 6;
          doc?.addImage(heightImgData, 'PNG', margin, yPos, maxWidth, (maxWidth * 0.45));
          yPos += (maxWidth * 0.45) + 10;
        } catch (chartError) {
          console.error('Error capturing height chart:', chartError);
        }
      }

      // Capture velocity chart if available
      if (velocityChartRef?.current && growthData?.velocityVsAge?.length > 0) {
        checkPageBreak(80);
        
        try {
          const velocityCanvas = await html2canvas(velocityChartRef?.current, {
            backgroundColor: '#ffffff',
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: false
          });
          const velocityImgData = velocityCanvas?.toDataURL('image/png');
          
          doc?.setFontSize(11);
          doc?.setFont('helvetica', 'bold');
          doc?.text('Height Velocity Over Time', margin, yPos);
          yPos += 6;
          doc?.addImage(velocityImgData, 'PNG', margin, yPos, maxWidth, (maxWidth * 0.45));
          yPos += (maxWidth * 0.45) + 10;
        } catch (chartError) {
          console.error('Error capturing velocity chart:', chartError);
        }
      }

      // Current Phase Section
      addSectionHeader('Current Maturation Phase');
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'bold');
      doc?.text(`Phase: ${growthPhase?.description || 'Unknown'}`, margin, yPos);
      yPos += 5;
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(9);
      doc?.text(`Confidence: ${growthPhase?.confidence || 'Unknown'}`, margin, yPos);
      yPos += 10;

      // What This Stage Means Section
      addSectionHeader('What This Stage Means');
      const confidenceGrade = dataQuality?.grade || 'D';
      const stageMeaning = getStageMeaning(growthPhase?.phase || 'unknown', confidenceGrade);
      
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      stageMeaning?.forEach(bullet => {
        checkPageBreak(10);
        addWrappedText(`• ${bullet}`, 9, false, margin + 2, 1.3);
        yPos += 2;
      });
      yPos += 5;

      // Training Priorities Section
      addSectionHeader('Training Priorities Now');
      const priorities = getTrainingPriorities(growthPhase?.phase || 'unknown', confidenceGrade, trainingExperience);
      
      priorities?.forEach(priority => {
        checkPageBreak(30);
        doc?.setFontSize(10);
        doc?.setFont('helvetica', 'bold');
        doc?.text(priority?.domain, margin, yPos);
        yPos += 5;
        
        doc?.setFontSize(8);
        doc?.setFont('helvetica', 'bold');
        doc?.text('Focus:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(priority?.focus, 8, false, margin + 4, 1.2);
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('Why:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(priority?.why, 8, false, margin + 4, 1.2);
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('What it looks like:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(priority?.whatItLooksLike, 8, false, margin + 4, 1.2);
        yPos += 4;
      });

      // Example Week Template Section
      addSectionHeader('Example Week Template');
      const templates = getExampleWeekTemplates(growthPhase?.phase || 'unknown', confidenceGrade, trainingExperience);
      const selectedTemplate = child?.sportTags?.some(tag => ['football', 'rugby', 'basketball', 'cricket']?.includes(tag))
        ? templates?.teamSport
        : templates?.endurance;
      
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(80, 80, 80);
      addWrappedText('These are examples, not prescriptions. Adapt based on technique, fatigue, and supervision.', 8, false, margin, 1.2);
      doc?.setTextColor(0, 0, 0);
      yPos += 3;

      doc?.setFont('helvetica', 'normal');
      selectedTemplate?.forEach(day => {
        checkPageBreak(6);
        addWrappedText(`• ${day}`, 8, false, margin + 2, 1.2);
        yPos += 1;
      });
      yPos += 6;

      // Example Sessions Section
      addSectionHeader('Example Sessions');
      const selectedSessions = [
        exampleSessions?.find(s => s?.id === 'strength_fundamentals'),
        exampleSessions?.find(s => s?.id === 'sprint_mechanics'),
        exampleSessions?.find(s => s?.id === 'aerobic_base')
      ]?.filter(Boolean);

      selectedSessions?.forEach(session => {
        checkPageBreak(45);
        doc?.setFontSize(10);
        doc?.setFont('helvetica', 'bold');
        doc?.text(session?.title, margin, yPos);
        yPos += 6;
        
        doc?.setFontSize(8);
        doc?.setFont('helvetica', 'bold');
        doc?.text('Goal:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(session?.goal, 8, false, margin + 4, 1.2);
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('Warm-up:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(session?.warmUp, 8, false, margin + 4, 1.2);
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('Main Set:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        session?.mainSet?.forEach(item => {
          checkPageBreak(6);
          addWrappedText(`- ${item}`, 8, false, margin + 4, 1.2);
          yPos += 1;
        });
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('Cool-down:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(session?.coolDown, 8, false, margin + 4, 1.2);
        yPos += 2;
        
        doc?.setFont('helvetica', 'bold');
        doc?.text('Progression:', margin + 2, yPos);
        yPos += 4;
        doc?.setFont('helvetica', 'normal');
        addWrappedText(session?.progression, 8, false, margin + 4, 1.2);
        yPos += 6;
      });

      // Safety Statements Section
      checkPageBreak(35);
      doc?.setFillColor(220, 220, 220);
      doc?.rect(margin, yPos, maxWidth, 8, 'F');
      doc?.setFontSize(11);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(0, 0, 0);
      doc?.text('Important Safety Statements', margin + 2, yPos + 5.5);
      yPos += 12;

      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'normal');
      safetyCaveats?.forEach(caveat => {
        checkPageBreak(10);
        addWrappedText(`• ${caveat}`, 8, false, margin + 2, 1.3);
        yPos += 2;
      });
      yPos += 6;

      // Evidence References Section
      addSectionHeader('Evidence References');
      doc?.setFontSize(7);
      doc?.setFont('helvetica', 'normal');
      evidenceReferences?.forEach(ref => {
        checkPageBreak(12);
        doc?.setFont('helvetica', 'bold');
        addWrappedText(ref?.title, 7, false, margin, 1.2);
        yPos += 1;
        doc?.setFont('helvetica', 'normal');
        doc?.setTextColor(80, 80, 80);
        addWrappedText(`${ref?.source}, ${ref?.year}`, 7, false, margin, 1.2);
        yPos += 1;
        addWrappedText(ref?.link, 7, false, margin, 1.2);
        doc?.setTextColor(0, 0, 0);
        yPos += 3;
      });

      // Save PDF
      doc?.save(`${child?.nickname}_Training_Insights_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  const getPhaseIcon = (phase) => {
    const icons = {
      'pre_phv': 'Sunrise',
      'around_phv': 'TrendingUp',
      'post_phv_early': 'Sun',
      'post_phv_late': 'Sunset',
      'unknown': 'HelpCircle'
    };
    return icons?.[phase] || 'HelpCircle';
  };

  const getPhaseColor = (phase) => {
    const colors = {
      'pre_phv': 'text-info',
      'around_phv': 'text-warning',
      'post_phv_early': 'text-success',
      'post_phv_late': 'text-primary',
      'unknown': 'text-text-secondary'
    };
    return colors?.[phase] || 'text-text-secondary';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading insights...</p>
        </div>
      </div>
    );
  }

  const confidenceGrade = dataQuality?.grade || 'D';
  const currentPhase = growthPhase?.phase || 'unknown';
  const stageMeaning = getStageMeaning(currentPhase, confidenceGrade);
  const priorities = getTrainingPriorities(currentPhase, confidenceGrade, trainingExperience);
  const templates = getExampleWeekTemplates(currentPhase, confidenceGrade, trainingExperience);
  const loadRules = getLoadManagementRules(currentPhase, confidenceGrade);

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Button 
            onClick={onBack}
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
          >
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-text-primary ml-4">Training Insights & Guidance</h1>
        </div>
        <Button
          onClick={exportToPDF}
          variant="outline"
          size="sm"
          iconName="Download"
          loading={exportingPDF}
        >
          Export PDF
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">For {child?.nickname}</h2>
          <p className="text-text-secondary text-sm">
            Current Age: {calculateAgeInYears(child?.dateOfBirth, new Date())?.toFixed(1)} years
          </p>
        </div>
      </div>

      {/* Training Experience Selector */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-3">Training Experience Level</h3>
        <p className="text-text-secondary text-sm mb-4">Select the level that best describes current training background (affects example complexity):</p>
        <div className="flex gap-3">
          <Button
            variant={trainingExperience === 'new' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTrainingExperience('new')}
          >
            New to Structured Training
          </Button>
          <Button
            variant={trainingExperience === 'some_experience' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTrainingExperience('some_experience')}
          >
            Some Experience
          </Button>
          <Button
            variant={trainingExperience === 'well_trained' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTrainingExperience('well_trained')}
          >
            Well Trained
          </Button>
        </div>
      </div>

      {/* Hidden chart containers for PDF export */}
      {growthData?.heightVsAge?.length > 0 && (
        <div style={{ position: 'absolute', left: '-9999px', width: '800px', height: '400px' }}>
          <div style={{ width: '100%', height: '100%', backgroundColor: 'white', padding: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={growthData?.heightVsAge} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="ageInYears" 
                  label={{ value: 'Age (years)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 'bold' } }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                  domain={['dataMin', 'dataMax']}
                />
                <YAxis 
                  domain={['dataMin - 5', 'dataMax + 5']}
                  label={{ value: 'Height (cm)', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 'bold' } }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip />
                <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '10px' }} />
                <Line 
                  type="monotone" 
                  dataKey="heightCm" 
                  stroke="#2563EB" 
                  strokeWidth={3} 
                  dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }} 
                  name="Height (cm)" 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {growthData?.velocityVsAge?.length > 0 && (
        <div style={{ position: 'absolute', left: '-9999px', width: '800px', height: '400px' }}>
          <div style={{ width: '100%', height: '100%', backgroundColor: 'white', padding: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={growthData?.velocityVsAge} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="ageInYears" 
                  label={{ value: 'Age (years)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 'bold' } }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                  domain={['dataMin', 'dataMax']}
                />
                <YAxis 
                  domain={['dataMin', 'dataMax + 2']}
                  label={{ value: 'Velocity (cm/year)', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 'bold' } }}
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip />
                <Legend verticalAlign="top" height={36} wrapperStyle={{ paddingBottom: '10px' }} />
                <Line 
                  type="monotone" 
                  dataKey="velocityCmPerYear" 
                  stroke="#059669" 
                  strokeWidth={3} 
                  dot={{ fill: '#059669', strokeWidth: 2, r: 4 }} 
                  name="Velocity (cm/year)" 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Current Phase Display */}
      {growthPhase && (
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="flex items-start mb-6">
            <div className={`bg-primary/10 p-4 rounded-full mr-4`}>
              <Icon name={getPhaseIcon(growthPhase?.phase)} className={`w-10 h-10 ${getPhaseColor(growthPhase?.phase)}`} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                {growthPhase?.description}
              </h2>
              <p className="text-text-secondary">
                Current developmental phase classification
                {growthPhase?.confidence && (
                  <span className="ml-2 text-xs font-medium capitalize">({growthPhase?.confidence} confidence)</span>
                )}
              </p>
              <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-info/10 text-info border border-info">
                <Icon name="Info" className="w-3 h-3 mr-1" />
                Confidence Grade: {confidenceGrade}
              </div>
            </div>
          </div>

          {/* Section 1: What This Stage Means */}
          <div className="bg-surface rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <Icon name="Lightbulb" className="w-5 h-5 mr-2 text-warning" />
              What This Stage Means
            </h3>
            <ul className="space-y-2">
              {stageMeaning?.map((bullet, idx) => (
                <li key={idx} className="flex items-start text-text-secondary">
                  <span className="text-primary mr-2 mt-1">•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* PHV Detection Details */}
          {phvResult?.status === 'detected' && (
            <div className="bg-success/10 border border-success rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-text-primary mb-3">PHV Detection Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-text-secondary text-sm mb-1">Estimated PHV Age</p>
                  <p className="text-text-primary font-medium text-lg">{phvResult?.phvAge?.toFixed(1)} years</p>
                </div>
                <div>
                  <p className="text-text-secondary text-sm mb-1">Age Range</p>
                  <p className="text-text-primary font-medium text-lg">
                    {phvResult?.phvAgeRange?.earliest?.toFixed(1)} - {phvResult?.phvAgeRange?.latest?.toFixed(1)} years
                  </p>
                </div>
                <div>
                  <p className="text-text-secondary text-sm mb-1">Peak Velocity</p>
                  <p className="text-text-primary font-medium text-lg">{phvResult?.peakVelocity?.toFixed(1)} cm/year</p>
                </div>
                <div>
                  <p className="text-text-secondary text-sm mb-1">Confidence Level</p>
                  <p className="text-text-primary font-medium text-lg capitalize">{phvResult?.confidence}</p>
                </div>
              </div>
              {phvResult?.confidenceGrade === 'C' && (
                <div className="mt-4 p-3 bg-warning/10 border border-warning rounded text-sm text-text-primary">
                  <Icon name="AlertTriangle" className="w-4 h-4 inline mr-2 text-warning" />
                  <strong>CAUTION:</strong> Low confidence grade. PHV estimate has wider uncertainty due to data quality limitations.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Section 2: Training Priorities Now */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Target" className="w-6 h-6 mr-2 text-primary" />
          Training Priorities Now
        </h2>
        <div className="space-y-6">
          {priorities?.map((priority, idx) => (
            <div key={idx} className="bg-surface rounded-lg p-5">
              <h3 className="text-lg font-semibold text-text-primary mb-3">{priority?.domain}</h3>
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-primary">Focus: </span>
                  <span className="text-text-secondary">{priority?.focus}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-primary">Why: </span>
                  <span className="text-text-secondary">{priority?.why}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-primary">What it looks like: </span>
                  <span className="text-text-secondary">{priority?.whatItLooksLike}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3: Example Week Templates */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Calendar" className="w-6 h-6 mr-2 text-primary" />
          Example Week Templates
        </h2>
        <p className="text-text-secondary text-sm mb-4">
          These are examples, not prescriptions. Adapt based on technique, fatigue, supervision, and enjoyment.
        </p>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-surface rounded-lg p-5">
            <h3 className="text-lg font-semibold text-text-primary mb-3">Template A: Team Sport Athlete</h3>
            <ul className="space-y-2">
              {templates?.teamSport?.map((day, idx) => (
                <li key={idx} className="text-text-secondary text-sm">
                  {day}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-surface rounded-lg p-5">
            <h3 className="text-lg font-semibold text-text-primary mb-3">Template B: Endurance or Mixed Sport Athlete</h3>
            <ul className="space-y-2">
              {templates?.endurance?.map((day, idx) => (
                <li key={idx} className="text-text-secondary text-sm">
                  {day}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Section 4: Example Sessions Library */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="BookOpen" className="w-6 h-6 mr-2 text-primary" />
          Example Sessions Library
        </h2>
        <p className="text-text-secondary text-sm mb-4">
          Click each session to expand full details. These are examples with ranges – not personalised prescriptions.
        </p>
        <div className="space-y-4">
          {exampleSessions?.map(session => {
            const isExpanded = expandedSessions?.includes(session?.id);
            return (
              <div key={session?.id} className="bg-surface rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => toggleSessionExpanded(session?.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-surface/80 transition-colors"
                >
                  <div className="flex items-center">
                    <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} className="w-5 h-5 mr-2 text-primary" />
                    <h3 className="text-lg font-semibold text-text-primary text-left">{session?.title}</h3>
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div>
                      <span className="text-sm font-medium text-primary">Goal: </span>
                      <span className="text-text-secondary text-sm">{session?.goal}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-primary">Warm-up: </span>
                      <span className="text-text-secondary text-sm">{session?.warmUp}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-primary block mb-2">Main Set:</span>
                      <ul className="space-y-1 ml-4">
                        {session?.mainSet?.map((item, idx) => (
                          <li key={idx} className="text-text-secondary text-sm">{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-primary">Cool-down: </span>
                      <span className="text-text-secondary text-sm">{session?.coolDown}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-success">Progression: </span>
                      <span className="text-text-secondary text-sm">{session?.progression}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-warning">Regression: </span>
                      <span className="text-text-secondary text-sm">{session?.regression}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 5: Load Management Rules */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="Shield" className="w-6 h-6 mr-2 text-primary" />
          Load Management Rules – Stage Specific
        </h2>
        <ul className="space-y-2">
          {loadRules?.map((rule, idx) => (
            <li key={idx} className="flex items-start text-text-secondary">
              <span className="text-primary mr-2 mt-1">•</span>
              <span>{rule}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 p-4 bg-info/10 border border-info rounded-lg">
          <p className="text-sm text-text-primary">
            <Icon name="Info" className="w-4 h-4 inline mr-2" />
            <strong>Remember:</strong> Pain does not equal harm, but persistent pain needs attention. If a new pain persists beyond 14 days, seek qualified assessment.
          </p>
        </div>
      </div>

      {/* Safety Caveats */}
      <div className="bg-warning/10 border border-warning rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
          <Icon name="AlertTriangle" className="w-5 h-5 mr-2 text-warning" />
          Important Safety Statements
        </h2>
        <ul className="space-y-2">
          {safetyCaveats?.map((caveat, idx) => (
            <li key={idx} className="flex items-start text-text-primary text-sm">
              <span className="text-warning mr-2 mt-1">•</span>
              <span>{caveat}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Evidence References */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
          <Icon name="BookMarked" className="w-5 h-5 mr-2 text-primary" />
          Evidence References
        </h2>
        <ul className="space-y-2">
          {evidenceReferences?.map((ref, idx) => (
            <li key={idx} className="text-text-secondary text-sm">
              <strong>{ref?.title}</strong> ({ref?.source}, {ref?.year})<br />
              <a href={ref?.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs">
                {ref?.link}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-info/10 border border-info rounded-lg p-6">
        <div className="flex items-start">
          <Icon name="Info" className="w-6 h-6 text-info mr-3 mt-1 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-3">Important Reminders</h3>
            <ul className="space-y-2 text-text-secondary">
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>This guidance is evidence-based but not prescriptive. Every child develops at their own pace.</span>
              </li>
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>These insights are for general sport and exercise guidance only, not medical advice.</span>
              </li>
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>Maturity estimates are shown with uncertainty and should not be treated as definitive.</span>
              </li>
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>The app does not provide medical diagnosis, injury prediction, or personalised training plans.</span>
              </li>
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>Continue regular measurements for more accurate PHV detection and phase classification.</span>
              </li>
              <li className="flex items-start">
                <span className="text-info mr-2 mt-1">•</span>
                <span>Consult qualified coaches and healthcare professionals for personalised advice.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Appropriate Guidance Prompts</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="Calendar" className="w-5 h-5 text-primary mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Monitoring Prompts</h4>
            </div>
            <p className="text-text-secondary text-sm">
              Continue regular height measurements monthly, or fortnightly during rapid growth phases.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="TrendingUp" className="w-5 h-5 text-warning mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Load Progression Caution</h4>
            </div>
            <p className="text-text-secondary text-sm">
              During rapid growth, progress training load gradually and monitor for signs of overload.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="Activity" className="w-5 h-5 text-success mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Movement Quality & Strength</h4>
            </div>
            <p className="text-text-secondary text-sm">
              Emphasise proper technique and fundamental movement patterns across all phases.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="Moon" className="w-5 h-5 text-info mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Recovery & Sleep</h4>
            </div>
            <p className="text-text-secondary text-sm">
              Ensure adequate sleep and recovery, especially during periods of rapid growth.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="AlertCircle" className="w-5 h-5 text-error mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Pain Persistence</h4>
            </div>
            <p className="text-text-secondary text-sm">
              If pain persists or worsens, seek professional medical advice promptly.
            </p>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-start mb-2">
              <Icon name="AlertTriangle" className="w-5 h-5 text-warning mr-2 mt-0.5" />
              <h4 className="font-medium text-text-primary">Data Quality & Confidence</h4>
            </div>
            <p className="text-text-secondary text-sm">
              {dataQuality?.status === 'sufficient' ? (
                <span>Good data quality. PHV detection and phase classification are reliable.</span>
              ) : (
                <span>Consider additional measurements to improve data quality and confidence in maturity estimates.</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightsScreen;